import { useState, useEffect, useCallback } from 'react';
import { Loader2, CheckCircle2, ExternalLink } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useAuthStore } from '@/stores/authStore';
import { useOnboardingDataStore } from '@/stores/onboardingDataStore';
import { cn } from '@/lib/utils';
import { updateConnectionStatus, getConnectionLink, type ConnectedPlatform } from '@/services/data/socialConnections';
import { getUserById } from '@/services/data/users';
import { toast } from 'sonner';
import { openMetricoolUrl } from '@/utils/metricoolNavigation';

interface NetworkConnectionStepProps {
  readonly onComplete: () => void;
  readonly setLoading: (loading: boolean) => void;
}

const networkConfig = [
  { id: 'instagram', label: 'Instagram', advice: 'Compte perso' },
  { id: 'youtube', label: 'YouTube', advice: 'Chaîne perso ou entreprise' },
  { id: 'linkedin', label: 'LinkedIn', advice: 'Compte perso' },
  { id: 'facebook', label: 'Facebook', advice: 'Page perso' },
  { id: 'tiktok', label: 'TikTok', advice: 'Compte perso' },
];

export function NetworkConnectionStep({
  onComplete,
  setLoading
}: NetworkConnectionStepProps) {
  const { userId } = useAuthStore();
  const socialPlatforms = useOnboardingDataStore((state) => state.socialPlatforms);
  const metricoolConnections = useOnboardingDataStore((state) => state.metricoolConnections);
  const connectionLinkState = useOnboardingDataStore((state) => state.connectionLinkState);
  const setSocialPlatforms = useOnboardingDataStore((state) => state.setSocialPlatforms);
  const setConnectionLinkSuccess = useOnboardingDataStore((state) => state.setConnectionLinkSuccess);
  const setConnectionLinkError = useOnboardingDataStore((state) => state.setConnectionLinkError);
  
  const [platforms, setPlatforms] = useState<ConnectedPlatform[]>(socialPlatforms);
  const [togglingPlatform, setTogglingPlatform] = useState<string | null>(null);
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);

  // Sync with preloaded data when it becomes available
  useEffect(() => {
    if (socialPlatforms.length > 0) {
      setPlatforms(socialPlatforms);
    }
  }, [socialPlatforms]);

  const handleToggle = useCallback(async (platformId: string, connectionId: string, newValue: boolean) => {
    setTogglingPlatform(platformId);
    
    // Optimistic update
    const updatedPlatforms = platforms.map(p => 
      p.id === connectionId ? { ...p, is_active: newValue } : p
    );
    setPlatforms(updatedPlatforms);
    setSocialPlatforms(updatedPlatforms);
    
    const result = await updateConnectionStatus(connectionId, { is_active: newValue });
    
    if (!result.success) {
      // Revert on error
      const revertedPlatforms = platforms.map(p => 
        p.id === connectionId ? { ...p, is_active: !newValue } : p
      );
      setPlatforms(revertedPlatforms);
      setSocialPlatforms(revertedPlatforms);
    }
    
    setTogglingPlatform(null);
  }, [platforms, setSocialPlatforms]);

  const handleConnectPlatform = useCallback(async (_networkId: string) => {
    if (!userId) return;
    setConnectingPlatform(_networkId);
    setLoading(true);
    try {
      let link: string | undefined;
      if (connectionLinkState.status === 'success' && connectionLinkState.link) {
        link = connectionLinkState.link;
      } else {
        const userResult = await getUserById(userId);
        if (!userResult.success || !userResult.data?.metricool_blog_id) {
          toast.error('Erreur', { description: 'Compte non configuré avec Metricool' });
          return;
        }
        const blogId = userResult.data.metricool_blog_id;
        const email = userResult.data.email ?? undefined;
        const result = await getConnectionLink(userId, blogId, { email });
        if (result.success && result.link) {
          setConnectionLinkSuccess(result.link);
          link = result.link;
        } else {
          const isTokenExpired = result.reason === 'token_expired';
          const errCode = isTokenExpired ? 'TOKEN_EXPIRED' : (result.error || 'Erreur inconnue');
          setConnectionLinkError(errCode);
          toast.error('Erreur', { description: errCode });
          return;
        }
      }
      openMetricoolUrl(link);
      toast.info('Connexion en cours', {
        description: 'Connectez vos réseaux dans le navigateur. Revenez ici après.',
      });
    } catch (error) {
      console.error('[NetworkConnectionStep] Connect error:', error);
      toast.error('Erreur', { description: "Impossible d'ouvrir le lien" });
    } finally {
      setConnectingPlatform(null);
      setLoading(false);
    }
  }, [userId, connectionLinkState, setConnectionLinkSuccess, setConnectionLinkError, setLoading]);

  // Map platforms by id for quick lookup
  const platformMap = new Map(platforms.map(p => [p.platform, p]));

  return (
    <div className="space-y-6">
      {/* Networks list */}
      <div className="flex flex-col gap-3">
        {networkConfig.map(network => {
          const platformData = platformMap.get(network.id);
          const isActive = platformData?.is_active ?? false;
          const metricoolStatus = metricoolConnections.get(network.id);
          const isConnectedMetricool = metricoolStatus?.connected ?? false;
          const isTikTok = network.id === 'tiktok';
          const isToggling = togglingPlatform === network.id;
          const isConnecting = connectingPlatform === network.id;

          const cardBackgroundClass = isConnectedMetricool
            ? "bg-warning/10 border-warning/30"
            : isActive
              ? "bg-white/[0.05] border-white/[0.1]"
              : "bg-white/[0.02] border-white/[0.06]";

          const textColorClass = isConnectedMetricool
            ? "text-warning"
            : isActive
              ? "text-white"
              : "text-white/50";

          return (
            <div
              key={network.id}
              className={cn(
                "flex flex-col gap-1.5 px-4 py-3 rounded-xl border transition-all",
                cardBackgroundClass
              )}
            >
              <div className="flex items-center gap-4">
                <Switch
                  checked={isActive}
                  onCheckedChange={(checked) => {
                    if (platformData) {
                      handleToggle(network.id, platformData.id, checked);
                    }
                  }}
                  disabled={isToggling || !platformData}
                />
                {/* Name + advice */}
                <div className="flex-1 min-w-0">
                  <span className={cn("text-sm font-medium", textColorClass)}>
                    {network.label}
                  </span>
                  {isActive && (
                    <p className={cn(
                      "text-xs truncate",
                      isConnectedMetricool ? "text-warning/60" : "text-white/30"
                    )}>
                      {network.advice}
                    </p>
                  )}
                </div>
                {/* Status / Connect button — always in the same block */}
                {isConnectedMetricool ? (
                  <div className="flex items-center gap-1.5 text-warning shrink-0">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span className="text-xs font-medium">Connecté</span>
                  </div>
                ) : isActive ? (
                  <button
                    onClick={() => handleConnectPlatform(network.id)}
                    disabled={isConnecting}
                    className={cn(
                      "flex items-center gap-1 text-xs font-medium text-warning shrink-0 hover:opacity-80 transition-opacity",
                      isConnecting && "opacity-50 cursor-wait"
                    )}
                  >
                    {isConnecting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <>
                        Connecter <ExternalLink className="h-3 w-3" />
                      </>
                    )}
                  </button>
                ) : null}
              </div>
              {isTikTok && isActive && !isConnectedMetricool && (
                <p className="text-xs text-white/40 ml-[52px]">
                  Pas de compte ? Créez-le 2 jours avant la 1ere diffusion.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-white/40 text-center">
        Activez les réseaux sur lesquels vous souhaitez publier. Vous pourrez les connecter plus tard.
      </p>
    </div>
  );
}
